import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TableFooter,
  Chip, Alert, CircularProgress, InputAdornment
} from '@mui/material';
import { ArrowBack, Print, Assessment, Search } from '@mui/icons-material';
import axios from 'axios';
import CompanyHeader from './CompanyHeader';
import { useDepartments } from '../hooks/useDepartments';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const LeaveReportPage = () => {
  const navigate = useNavigate();
  const { names: deptNames } = useDepartments();

  const [filterYear,   setFilterYear]   = useState(new Date().getFullYear().toString());
  const [filterDept,   setFilterDept]   = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');

  const years = [];
  for (let y = new Date().getFullYear() + 1; y >= 2022; y--) years.push(y.toString());

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/api/leave/report?year=${filterYear}`, { headers: hdrs() });
      setRows(res.data || []);
    } catch { setError('Failed to load leave report'); }
    finally { setLoading(false); }
  }, [filterYear]);

  useEffect(() => { load(); }, [load]);

  // Distinct leave types for filter dropdown
  const leaveTypes = useMemo(() => {
    const seen = new Map();
    rows.forEach(r => { if (!seen.has(r.leaveTypeCode)) seen.set(r.leaveTypeCode, r.leaveTypeName); });
    return [...seen.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => a.code.localeCompare(b.code));
  }, [rows]);

  const filtered = useMemo(() => {
    let r = rows;
    if (filterDept)   r = r.filter(x => x.department === filterDept);
    if (filterType)   r = r.filter(x => x.leaveTypeCode === filterType);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      r = r.filter(x =>
        x.employeeName.toLowerCase().includes(q) ||
        x.employeeNo.toLowerCase().includes(q)
      );
    }
    return r;
  }, [rows, filterDept, filterType, filterSearch]);

  // Column totals
  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    entitlement: acc.entitlement + (r.entitlement || 0),
    taken:       acc.taken       + (r.taken       || 0),
    recall:      acc.recall      + (r.recall      || 0),
    adjustments: acc.adjustments + (r.adjustments || 0),
    balance:     acc.balance     + (r.balance      || 0),
  }), { entitlement: 0, taken: 0, recall: 0, adjustments: 0, balance: 0 }), [filtered]);

  const balColor = (b) => b <= 0 ? 'error' : b <= 5 ? 'warning' : 'success';
  const fmtAdj   = (n) => n > 0 ? `+${n}` : n < 0 ? String(n) : '—';

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <Card elevation={3} className="no-print"
        sx={{ mb: 2, background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined"
                onClick={() => navigate('/hr/leave')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
                Back
              </Button>
              <Assessment />
              <Typography variant="h6" fontWeight={700}>Leave Report — {filterYear}</Typography>
              {!loading && (
                <Chip label={`${filtered.length} rows`} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700 }} />
              )}
            </Box>
            <Button variant="contained" startIcon={<Print />}
              onClick={() => window.print()}
              disabled={filtered.length === 0}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              Print / Save PDF
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Filters ── */}
      <Card elevation={2} className="no-print" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField select label="Year" value={filterYear} size="small" sx={{ minWidth: 110 }}
              onChange={e => setFilterYear(e.target.value)} InputLabelProps={{ shrink: true }}>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <TextField select label="Department" value={filterDept} size="small" sx={{ minWidth: 180 }}
              onChange={e => setFilterDept(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
              <MenuItem value=""><em>All Departments</em></MenuItem>
              {deptNames.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TextField select label="Leave Type" value={filterType} size="small" sx={{ minWidth: 180 }}
              onChange={e => setFilterType(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
              <MenuItem value=""><em>All Leave Types</em></MenuItem>
              {leaveTypes.map(lt => (
                <MenuItem key={lt.code} value={lt.code}>{lt.name}</MenuItem>
              ))}
            </TextField>
            <TextField placeholder="Search employee…" size="small" sx={{ minWidth: 210 }}
              value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            {loading && <CircularProgress size={20} />}
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} className="no-print">{error}</Alert>}

      <Card elevation={3}>
        <CardContent sx={{ p: 0 }}>

          {/* Letterhead */}
          <Box sx={{ p: 3, pb: 0 }}>
            <CompanyHeader title="Leave Report" subtitle={`${filterYear}${filterDept ? ` · ${filterDept}` : ''}${filterType ? ` · ${filterType}` : ''}`} />
          </Box>

          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                <TableCell sx={{ bgcolor: '#e8f5e9' }}><strong>Emp No</strong></TableCell>
                <TableCell sx={{ bgcolor: '#e8f5e9' }}><strong>Employee Name</strong></TableCell>
                <TableCell sx={{ bgcolor: '#e8f5e9' }}><strong>Department</strong></TableCell>
                <TableCell sx={{ bgcolor: '#e8f5e9' }}><strong>Leave Type</strong></TableCell>
                <TableCell align="right" sx={{ bgcolor: '#e8f5e9' }}><strong>Entitlement</strong></TableCell>
                <TableCell align="right" sx={{ bgcolor: '#e8f5e9' }}><strong>Taken</strong></TableCell>
                <TableCell align="right" sx={{ bgcolor: '#e8f5e9' }}><strong>Recalled</strong></TableCell>
                <TableCell align="right" sx={{ bgcolor: '#e8f5e9' }}><strong>Adjustments</strong></TableCell>
                <TableCell align="right" sx={{ bgcolor: '#e8f5e9' }}><strong>Balance</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      {loading ? 'Loading…' : 'No data for the selected filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map((r, i) => (
                <TableRow key={i} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">{r.employeeNo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.employeeName}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.jobTitle || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{r.department || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.leaveTypeName}</Typography>
                    <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                      {r.leaveTypeCode}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{r.entitlement || 0}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: r.taken > 0 ? '#c62828' : 'inherit' }}>
                      {r.taken > 0 ? `−${r.taken}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: r.recall > 0 ? '#1565c0' : 'inherit' }}>
                      {r.recall > 0 ? `+${r.recall}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: r.adjustments !== 0 ? '#e65100' : 'inherit' }}>
                      {fmtAdj(r.adjustments)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={`${r.balance}`} size="small" color={balColor(r.balance)}
                      sx={{ fontWeight: 700, minWidth: 52 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {filtered.length > 0 && (
              <TableFooter>
                <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" fontWeight={700}>TOTALS ({filtered.length} rows)</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700}>{totals.entitlement}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} color="#c62828">
                      {totals.taken > 0 ? `−${totals.taken}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} color="#1565c0">
                      {totals.recall > 0 ? `+${totals.recall}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} color="#e65100">
                      {fmtAdj(totals.adjustments)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={`${totals.balance}`} size="small"
                      color={balColor(totals.balance)} sx={{ fontWeight: 700 }} />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>

        </CardContent>
      </Card>
    </Box>
  );
};

export default LeaveReportPage;
